// Locked-module promo panel: animation, description, tutorial, price, store button.

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';
import ModulePreviewAnimation from './store/ModulePreviewAnimation';
import { formatListingPrice, getStoreListing, localizeStoreListing } from './store/storeListings';
import { buildStoreUrl } from './store/storeUrls';
import { getCostPanelTheme } from './store/storePanelTheme';
import { usePanelThemeMode } from './store/usePanelThemeMode';

export default function UpgradeCta({
  moduleCode,
  moduleName,
  description,
  projectSlug,
  returnTo,
  size = 'normal',
  context = 'admin',
}) {
  const navigate = useNavigate();
  const { lang, projectModules: pm, moduleStore: ms } = usePublicUiLocale();
  const themeMode = usePanelThemeMode();
  const panel = getCostPanelTheme(context, themeMode);
  const meta = pm.modules[moduleCode] || {};
  const listing = localizeStoreListing(getStoreListing(moduleCode), lang);
  const title = moduleName || meta.name || moduleCode;
  const blurb = listing?.tagline || description || meta.description || '';
  const compact = size === 'compact';
  const priceLabel = listing?.price?.monthly
    ? `${ms?.projectCostApprox || 'approx.'} ${formatListingPrice(listing, ms)}`
    : formatListingPrice(listing, ms);

  const storeUrl = projectSlug
    ? buildStoreUrl(projectSlug, { module: moduleCode, returnTo })
    : null;

  const handleGoToStore = () => {
    if (storeUrl) navigate(storeUrl);
  };

  const highlights = (listing?.highlights || []).slice(0, compact ? 2 : 4);
  const tutorialSteps = (listing?.tutorialSteps || []).slice(0, compact ? 2 : 3);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: compact ? 520 : 920,
        mx: 'auto',
        p: compact ? 2 : 3,
        borderRadius: 2,
        border: panel.border,
        bgcolor: panel.paperBg,
        color: panel.lineLabelColor,
      }}
    >
      <Stack
        direction={compact ? 'column' : { xs: 'column', md: 'row' }}
        spacing={compact ? 1.5 : 3}
        alignItems={compact ? 'stretch' : 'flex-start'}
      >
        <Box sx={{ flex: compact ? undefined : '0 0 42%', width: compact ? '100%' : undefined }}>
          <ModulePreviewAnimation
            animationKey={listing?.animationKey || moduleCode}
            compact={compact}
            mode={themeMode}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <LockIcon sx={{ color: panel.subtitleColor, fontSize: compact ? 20 : 24 }} />
            <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ color: panel.amountColor, fontWeight: 700 }}>
              {title}
            </Typography>
            <Chip
              size="small"
              label={pm.moduleNotConnected}
              variant="outlined"
              sx={{ color: panel.textPrimary, borderColor: '#455a64', ml: 'auto', '& .MuiChip-label': { color: panel.textPrimary } }}
            />
          </Stack>

          {blurb ? (
            <Typography variant="body2" sx={{ color: panel.lineLabelColor, mb: 1.5 }}>
              {blurb}
            </Typography>
          ) : null}

          {highlights.length > 0 ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: panel.textPrimary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                {ms?.highlights || 'Highlights'}
              </Typography>
              <Stack spacing={0.25}>
                {highlights.map((h) => (
                  <Typography key={h} variant="body2" sx={{ color: panel.textPrimary, fontSize: compact ? 12 : 14 }}>
                    • {h}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}

          {!compact && tutorialSteps.length > 0 ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: panel.textPrimary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                {ms?.tutorial || 'How it works'}
              </Typography>
              <Stack spacing={0.5}>
                {tutorialSteps.map((step, i) => (
                  <Typography key={step} variant="body2" sx={{ color: panel.textPrimary, fontSize: 13 }}>
                    {i + 1}. {step}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}

          <Stack
            direction={compact ? 'column' : 'row'}
            spacing={1.5}
            alignItems={compact ? 'stretch' : 'center'}
            sx={{ mt: compact ? 1 : 2 }}
          >
            <Typography variant={compact ? 'body2' : 'subtitle1'} sx={{ color: panel.lineValueColor, fontWeight: 600 }}>
              {priceLabel}
            </Typography>
            {storeUrl ? (
              <Button
                variant="outlined"
                startIcon={<StorefrontIcon />}
                onClick={handleGoToStore}
                size={compact ? 'small' : 'medium'}
                sx={{
                  color: panel.buttonColor,
                  borderColor: panel.buttonBorder,
                  ...(compact ? {} : { ml: 'auto' }),
                  '&:hover': { borderColor: panel.lineValueColor, bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                {ms?.goToStore || pm.wantConnect}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

UpgradeCta.propTypes = {
  moduleCode: PropTypes.string.isRequired,
  moduleName: PropTypes.string,
  description: PropTypes.string,
  projectSlug: PropTypes.string,
  returnTo: PropTypes.string,
  size: PropTypes.oneOf(['compact', 'normal']),
  context: PropTypes.oneOf(['admin', 'store']),
};
